import { formatZipCode } from '@/presenters/components/catalog/product/BuyBox/Shipments/utils';

import { MAIN_SLUGS, STEP_STATUS } from './constants';

export interface Item {
  lm: number;
  name: string;
  url: string;
  image: string;
  weight: number;
  quantity: number;
  unit: string;
  status: string;
  price: {
    subTotal: number;
    unit: number;
  };
  shopName: string;
}

export type StepStatus = (typeof STEP_STATUS)[keyof typeof STEP_STATUS];

export interface Step {
  date: string | null;
  slug: string;
  status: StepStatus;
  description: string;
  current: boolean;
}

export interface ShippingAddress {
  street: string;
  number: string;
  complement: string;
  district: string;
  city: string;
  state: string;
  postalCode: string;
  nickname: string;
  type: string;
  reference: string;
  storeName: string;
}

export interface Thread {
  createdAt: string;
  threadId: string;
  type: string;
  status: string;
  option: string;
  subOption: string | null;
}

export const SHIPTMENT_TYPES = {
  DELIVERY: 'delivery',
  PICKUP_IN_STORE: 'pickupInStore',
  STORE: 'store', // legacy
  DRIVE_THRU: 'driveThru', // legacy
  CHECKOUT: 'checkout', // legacy
} as const;

export interface Values {
  total: number;
  subTotal: number;
  shippingCost: number;
  discount: number;
  vouchers?: number;
}

export interface OrderLine {
  id: string;
  productSku: string;
}

export interface Invoice {
  accessKey: string;
  orderLines: OrderLine[];
  shopId: number;
  shopName: string;
}

export interface ShipmentData {
  type: (typeof SHIPTMENT_TYPES)[keyof typeof SHIPTMENT_TYPES];
  items: Item[];
  steps: Step[];
  shippingAddress: ShippingAddress;
  deliveryDate: {
    date: string;
    message: string;
  };
  delayTime: number;
  pinCode: string;
  id: string;
  trackingUrl: string;
  trackingCode: string;
  marketplaceOrderId?: string;
  threads: Thread[];
  values: Values;
  invoice?: Invoice;
}

export class Shipment {
  id;
  type;
  marketplaceOrderId;
  values;
  delayTime;
  invoice;
  steps;
  trackingCode;
  trackingUrl;
  pinCode;
  isDelivery;
  isWithdrawal;
  currentStep;
  isInTransit;
  isReadyForPickup;
  isFinished;
  isCancelled;
  isMarketplace;
  isFreeShipping;
  address;
  storeName;
  shopName;
  deliveryDate;
  items;
  hasThreads;

  constructor(private readonly data: ShipmentData) {
    this.id = data.id;
    this.type = data.type;
    this.marketplaceOrderId = data.marketplaceOrderId;
    this.values = data.values;
    this.delayTime = data.delayTime;
    this.invoice = data.invoice;
    this.steps = data.steps;
    this.trackingCode = data.trackingCode;
    this.trackingUrl = data.trackingUrl;
    this.pinCode = data.pinCode;
    this.isDelivery = this.#buildIsDelivery();
    this.isWithdrawal = this.#buildIsWithdrawal();
    this.currentStep = this.#buildCurrentStep();
    this.isInTransit = this.#buildIsInTransit();
    this.isReadyForPickup = this.#buildIsReadyForPickup();
    this.isFinished = this.#buildIsFinished();
    this.isCancelled = this.#buildIsCancelled();
    this.isMarketplace = this.#buildIsMarketplace();
    this.isFreeShipping = this.#buildIsFreeShipping();
    this.address = this.#buildAddress();
    this.storeName = this.#buildStoreName();
    this.shopName = this.#buildShopName();
    this.deliveryDate = this.#buildDeliveryDate();
    this.items = this.#buildItems();
    this.hasThreads = this.#buildHasThreads();
  }

  #buildIsDelivery() {
    return this.type === SHIPTMENT_TYPES.DELIVERY;
  }

  #buildIsWithdrawal() {
    return this.type !== SHIPTMENT_TYPES.DELIVERY;
  }

  #buildCurrentStep() {
    return this.steps.find((step) => step.current);
  }

  #buildIsInTransit() {
    return this.currentStep?.slug === MAIN_SLUGS.IN_TRANSIT;
  }

  #buildIsReadyForPickup() {
    return this.currentStep?.slug === MAIN_SLUGS.READY_FOR_PICKUP;
  }

  #buildIsFinished() {
    return (
      this.currentStep?.slug === MAIN_SLUGS.FINISHED &&
      this.currentStep?.status === STEP_STATUS.ACTIVE
    );
  }

  #buildIsCancelled() {
    return this.currentStep?.slug === MAIN_SLUGS.CANCELLED;
  }

  #buildIsMarketplace() {
    return Boolean(this.marketplaceOrderId);
  }

  #buildIsFreeShipping() {
    return this.values.shippingCost === 0;
  }

  #buildAddress() {
    const { street, number, complement, city, state, postalCode, district } =
      this.data.shippingAddress;

    return `${street} ${number}, ${complement} ${city}/${state} - ${formatZipCode(
      postalCode
    )} ${district}`;
  }

  #buildStoreName() {
    return this.data.shippingAddress.storeName;
  }

  #buildShopName() {
    return this.data.items[0].shopName;
  }

  #buildDeliveryDate() {
    return this.data.deliveryDate.date;
  }

  #buildItems() {
    return this.data.items.map((item) => ({
      id: item.lm,
      lm: item.lm,
      name: item.name,
      shopName: item.shopName,
      url: item.url,
      image: item.image,
      weight: item.weight,
      quantity: item.quantity,
      unit: item.unit,
      status: item.status,
      price: item.price,
    }));
  }

  #buildHasThreads() {
    return this.data.threads && this.data.threads.length > 0;
  }
}
